import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {TaAppStore} from '../state/clcta-store';
import {PermissionService} from './permission.service';
import {HomeTab} from '../util/app.types';

// Sends a user back to the dashboard they came from when they leave a TA.
//
// The remembered route (store.previousPageRoute, captured as router.url when a TA row is opened)
// always wins - it includes the home page's ?tab=, so the tab comes back for free. When it carries
// no tab of its own we derive one from the TA, which covers the flows that never recorded a route:
// a fresh page load, or a delegate TA created from the delegate tab and submitted straight away.
@Injectable({providedIn: 'root'})
export class ReturnNavigationService {
  private router = inject(Router);
  private store = inject(TaAppStore);
  private permissionService = inject(PermissionService);

  navigateBack(fallbackRoute: string): void {
    // navigateByUrl, not navigate([...]) - the latter encodes the whole url into one path segment.
    this.router.navigateByUrl(this.resolveReturnUrl(fallbackRoute));
  }

  resolveReturnUrl(fallbackRoute: string): string {
    const route = this.store.previousPageRoute() || fallbackRoute;
    return this.shouldReturnToDelegateTab(route) ? this.homeUrlForTab('delegate') : route;
  }

  // Only a bare /home is up for grabs. An explicit ?tab= is the user's own last choice and outranks
  // any guess we make here, and any other dashboard is where they actually came from.
  private shouldReturnToDelegateTab(route: string): boolean {
    const [path, query = ''] = route.split('?');
    if (path !== '/home' || new URLSearchParams(query).has('tab')) {
      return false;
    }
    // isDelegateForTa, not isDelegateTa: a finance admin with Global scope can view someone else's
    // delegate TA, and it will not be in their delegate tab. canCreateDelegateTa guards against
    // sending a user whose permission was revoked to a tab that no longer renders.
    return this.permissionService.canCreateDelegateTa() && this.permissionService.isDelegateForTa();
  }

  private homeUrlForTab(tab: HomeTab): string {
    return `/home?tab=${tab}`;
  }
}
